import Modal from "patient-ping-remedy/packages/modal";
import React, {useContext, useState} from "react";
import DropdownList from "patient-ping-remedy/packages/dropdown-list";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {css} from "@emotion/css";
import {CareJourneyDetailsResponse, CareJourneyType} from "@/api/dto/care-journey";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";
import {useActiveRoster} from "@/store/roster_store";
import {useAlertStore} from "@/store/alert_store";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {MixpanelEventType, MixpanelTaskClosedEvent} from "@/api/dto/mixpanel";
import {useMixpanel} from "@/hooks/useMixpanel";
import {FilterInvalidate} from "@common/FilterComponent";
import {useAuthStore} from "@/store/auth_store";

interface Props {
    close: () => void;
    careJourneyDetails: CareJourneyDetailsResponse;
    closeTaskEditDrawer: () => void;
    invalidate: FilterInvalidate;
}

const closeReasonOptions = [
  {label: 'Patient not willing to engage', value: 'patient_not_willing_to_engage'},
  {label: 'Unable to reach patient', value: 'unable_to_reach_patient'},
  {label: 'New TCM Eligible Event Occurred', value: 'new_tcm_eligible_event'},
  {label: 'Homelessness', value: 'homelessness'},
];

export const closeReasonsForScheduleAppointment = [
  'Provider Availability',
  'Copay/Cost',
  'Insurance',
  'Location',
  'Patient did not attend appointment-Willing to reschedule',
  'Patient did not attend appointment-Not willing/unable to reschedule',
  'Waitlisted',
  'Appointment not needed',
  'Could not reach the practice',
];

export default function ConfirmCloseModal(props: Props) {
  const currentRoster = useActiveRoster();
  const {currentUser} = useAuthStore();
  const {addAlert} = useAlertStore();
  const queryClient = useQueryClient();
  const {carecoApi} = useContext(CarecoApiContext);
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const [closeReason, setCloseReason] = useState<{ value: string; label: string } | null>(null);

  const isScheduleAppointmentJourney = props.careJourneyDetails.journeyType === CareJourneyType.BH_SCHEDULE_APPOINTMENT;

  const mutationFunction = async (reason: string) => {
    if(isScheduleAppointmentJourney) {
      return await carecoApi!.cancelAppointment(props.careJourneyDetails.careJourneyInstanceId, currentRoster!.id, {
        journey_id: props.careJourneyDetails.careJourneyInstanceId,
        reason,
        canceledBy: currentUser!.id,
        disposition: 'Closed'
      });
    }

    return await carecoApi!.closeCareJourney(props.careJourneyDetails.careJourneyInstanceId,
          currentRoster!.id,
          reason);
  };

  const {mutate: closeTask, isPending: isClosing} = useMutation({
    mutationFn: (reason: string) => mutationFunction(reason),
    onSuccess: (_, reason) => {
      props.invalidate();
      if (mixpanelEventProperties) {
        const event: MixpanelTaskClosedEvent = {
          eventName: MixpanelEventType.BIH_TASK_CLOSED,
          eventType: props.careJourneyDetails.eventType,
          careJourneyName: props.careJourneyDetails.journeyType,
          closeReason: reason,
          ...mixpanelEventProperties!,
          ...getCurrentGroupDetails(currentRoster!)
        };
        carecoApi?.postMixpanelEvent(event);
      }
      addAlert({content: "Closed the task successfully", type: ToastType.SUCCESS});
      props.close();
      props.closeTaskEditDrawer();
      void queryClient.invalidateQueries({queryKey: ["careJourneys"]});
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to close Task ${Helpers.traceId(error)}`, error);
    },
  });

  return (
    <Modal className={css({width: '70rem'})}
      headerText={"Reason for closing"}

      buttons={[
        <Button key="cancel" onClick={props.close} styleType={StyleType.TERTIARY}>
           Cancel
        </Button>,
        <Button
          testId="closes-task-button"
          key="close"
          disabled={!closeReason || isClosing}
          onClick={() => closeReason && closeTask(closeReason?.value)}
        >
           Close
        </Button>,
      ]}
    >
      <DropdownList
        required
        items={isScheduleAppointmentJourney ?
          closeReasonsForScheduleAppointment.map(label => ({label, value: label})) : closeReasonOptions}
        id={'close-reason'}
        isClearable={true}
        menuPlacement={'auto'}
        placeholder={'Select reason'}
        label={'Reason for closing'}
        value={closeReason}
        disabled={isClosing}
        handleChange={(option: { value: string; label: string } | null) => setCloseReason(option)}
        isWithinModal
      />
    </Modal>
  );

}
